import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useCreateUserBo, useUpdateUserBo, useUpdateUserPasswordBo } from "@/api/user.api";
import { Button } from "@/components/ui/button";
import { UserCreateModal } from "@/components/shared/user/UserCreateModal";
import { UserUpdateModal } from "@/components/shared/user/UserUpdateModal";
import { userAtom } from "@/stores/authStore";
import { GeneralInfoUserTable } from "@/components/backOffice/parameters/generalInfoUserTable";
import { GetAllCompanyDto } from "@/types/api/api-types";
import { ParametersHeader } from "@/components/shared/parametersHeader";
import Paths from "@/constants/paths";
import { useAtomValue } from "jotai";

type ModalState =
  | { type: "closed" }
  | { type: "create" }
  | { type: "update"; user: GetAllCompanyDto["user"][number] };

export const GeneralInfoUsersParameterPage = () => {
  const { t } = useTranslation("boParameters");
  const [modalState, setModalState] = useState<ModalState>({ type: "closed" });

  const createUserMutation = useCreateUserBo();
  const updateUserMutation = useUpdateUserBo();
  const updatePasswordMutation = useUpdateUserPasswordBo();
  const user = useAtomValue(userAtom);
  const navigate = useNavigate();

  const handleClose = () => setModalState({ type: "closed" });

  const handleCreateUser = () => {
    setModalState({ type: "create" });
  };

  const handleEdit = (user: GetAllCompanyDto["user"][number]) => {
    setModalState({ type: "update", user });
  };

  return (
    <ParametersHeader>
      <div className="flex w-full flex-1 flex-col gap-6 lg:px-8">
        <div className="flex flex-row justify-between gap-2 pb-5">
          <div className="items-left flex flex-col gap-2">
            <h2 className="text-lg font-semibold">{t("usersPage.title")}</h2>
            <p className="text-sm text-gray-600">{t("usersPage.description")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {user?.role === "SUPERADMIN" && (
              <Button onClick={() => navigate(Paths.bo.parametersLogs)} variant="outline">
                {t("usersPage.logPermissions")}
              </Button>
            )}
            <Button onClick={handleCreateUser}>
              <Plus className="h-4 w-4" />
              {t("usersPage.addUser")}
            </Button>
          </div>
        </div>
        <GeneralInfoUserTable handleEdit={handleEdit} />
        <UserCreateModal
          open={modalState.type === "create"}
          onClose={handleClose}
          createUserMutation={createUserMutation}
          isBackOffice
        />
        {modalState.type === "update" && (
          <UserUpdateModal
            user={modalState.user}
            open
            onClose={handleClose}
            updateUserMutation={updateUserMutation}
            updatePasswordMutation={updatePasswordMutation}
            isBackOffice
          />
        )}
      </div>
    </ParametersHeader>
  );
};
